/**
 * Hall of Honor (08 §2.10, 04 §5): up to six living veterans with their full dossiers, and the
 * roll of honour of those retired. RETIRE is one of the two places the menus ask "are you
 * sure": a retired ranger can never be lost - or used - again.
 */

import './rosters.css';
import type { RangerRecord } from '../../meta/types';
import type { Ctx, Screen } from './context';
import { box, button, el, errorMessage, panel } from './dom';
import { fullName, rankAbbr, serviceName } from './text';
import { confirm, notice } from './widgets/confirm';
import { dossierCard } from './widgets/dossierCard';
import { frame } from './widgets/frame';

/** The Hall holds six, as the original's roster did. */
export const HALL_SLOTS = 6;

function rollLine(r: RangerRecord): HTMLElement {
  const call = r.callsign.trim() === '' ? '' : ` "${r.callsign}"`;
  return box(
    'menu-roll-line',
    el('span', 'menu-paper', `${rankAbbr(r.rank)} ${fullName(r)}${call}`),
    el('span', 'menu-dim', `${r.record.missions} MSN`),
  );
}

export function hallScreen(ctx: Ctx): Screen {
  const profile = ctx.api.profile();
  const f = frame('hall', 'HALL OF HONOR');
  f.info.append(el('span', '', `${profile.hall.length}/${HALL_SLOTS} LIVING VETERANS`));

  const retire = (r: RangerRecord): void =>
    confirm(ctx, {
      title: `RETIRE ${serviceName(r)}?`,
      lines: [
        'THIS IS PERMANENT. HE LEAVES THE HALL FOR THE ROLL OF HONOUR.',
        'A RETIRED RANGER CAN NEVER BE LOST - OR USED - AGAIN.',
      ],
      confirmLabel: 'RETIRE HIM',
      danger: true,
      onConfirm: () => {
        try {
          ctx.api.retireVeteran(r.id);
        } catch (err) {
          notice(ctx, 'NOT RETIRED', [errorMessage(err)]);
          return;
        }
        ctx.nav.hall();
      },
    });

  const grid = el('div', 'menu-card-grid menu-card-grid-2 menu-scroll menu-grow');
  for (const r of profile.hall) {
    const card = dossierCard(r, { describe: ctx.describe });
    card.append(box('menu-dossier-actions', button('RETIRE', () => retire(r), 'menu-btn-small menu-btn-danger')));
    grid.append(card);
  }
  if (profile.hall.length === 0) {
    grid.append(
      el('div', 'menu-empty menu-prose', 'NO VETERANS YET. WIN AN OPERATION AND ONE SURVIVOR IS INDUCTED HERE.'),
    );
  }

  const roll = panel('ROLL OF HONOUR - RETIRED', 'menu-grow');
  for (const r of profile.retired) roll.body.append(rollLine(r));
  if (profile.retired.length === 0) roll.body.append(el('div', 'menu-dim', 'NOBODY HAS RETIRED YET.'));

  const back = button('BACK', () => ctx.nav.title());
  const note = el(
    'div',
    'menu-prose menu-dim menu-small',
    'VETERANS MAY JOIN A NEW OPERATION. THEY ARE AS MORTAL THERE AS ANY RECRUIT.',
  );
  f.body.append(box('menu-col menu-grow', grid), box('menu-col menu-side', roll.panel, note, back));
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => back };
}
